import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { motion } from "framer-motion";

const faqs = [
    {
        q: "What is a real estate agent referral service?",
        a: "A real estate agent referral service connects home buyers or sellers with qualified real estate professionals. The service evaluates agents based on experience, performance, and local knowledge, then introduces clients to one or more agents who are a good match for their needs.",
    },
    {
        q: "How does RealtorReferral.ca work?",
        a: "Clients start by completing a short questionnaire about their real estate goals, location, and timeline. Based on this information, RealtorReferral.ca identifies up to three real estate agents who are well suited to help. Clients can then speak with the agents and choose the one they feel most comfortable working with.",
    },
    {
        q: "Is the referral service free for buyers and sellers?",
        a: "Yes. The service is free for buyers and sellers. When a transaction successfully closes, the real estate agent pays a referral fee to RealtorReferral.ca. This fee comes from the agent's commission and does not increase the client's cost.",
    },
    {
        q: "How are the real estate agents selected?",
        a: "Agents are selected based on several factors, including experience, professionalism, local market knowledge, communication style, and past client results. The goal is to connect clients with agents who are both competent and a good personal fit.",
    },
    {
        q: "Do I have to work with the agent you recommend?",
        a: "No. You are never obligated to work with any agent introduced through the service. The purpose of the introduction is to help you evaluate your options and choose the agent you feel most comfortable with.",
    },
    {
        q: "How many agents will I be introduced to?",
        a: "Typically clients are introduced to up to three agents. This allows clients to compare approaches and personalities before selecting the best fit.",
    },
    {
        q: "What makes using a referral service better than finding an agent online?",
        a: "Online searches often rely on advertising or reviews that may not tell the full story. RealtorReferral.ca provides a more personalized approach by evaluating your needs and recommending agents who have been carefully screened for professionalism and experience.",
    },
    {
        q: "Can you help me find a realtor in another city or province?",
        a: "Yes. If you are moving to another city or province, RealtorReferral.ca can connect you with trusted agents in many locations. This is particularly helpful for people relocating who do not already know local real estate professionals.",
    },
    {
        q: "What if I'm not satisfied with the agent I choose?",
        a: "If you feel the agent is not the right fit, you can contact us and request another introduction. Our goal is to help you find an agent who understands your needs and communicates well with you.",
    },
    {
        q: "Are the agents part of your company?",
        a: "No. The agents remain independent professionals who work with their own real estate brokerages. The referral service simply introduces clients to agents who meet certain standards.",
    },
    {
        q: "Do you only work with experienced agents?",
        a: "In most cases we prioritize agents with strong track records and proven experience in their local markets. Experience helps ensure clients receive knowledgeable guidance throughout the buying or selling process.",
    },
    {
        q: "What types of clients use a referral service?",
        a: "Clients who often benefit from a referral service include people moving to a new city, buyers who want help choosing a reliable agent, sellers who want a strong listing agent, and people who have had a poor experience with agents in the past.",
    },
    {
        q: "How long does it take to receive agent recommendations?",
        a: "Most clients receive introductions within one to three business days after submitting their questionnaire.",
    },
    {
        q: "Is my personal information kept confidential?",
        a: "Yes. Your information is only used to help match you with suitable real estate agents and is not shared publicly.",
    },
    {
        q: "Why was RealtorReferral.ca created?",
        a: "RealtorReferral.ca was created to make it easier for buyers and sellers to find trustworthy real estate professionals without relying solely on online searches, advertising, or referrals from 'a friend-of-a-friend'.",
    },
];

export default function FaqPage() {
    return (
        <div className="bg-brand-paper" data-testid="faq-page">
            <section className="mx-auto max-w-5xl px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — Frequently asked
                    </div>
                    <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight text-brand-ink">
                        Questions, <span className="italic">honestly answered</span>
                    </h1>
                    <p className="mt-8 text-lg text-brand-muted max-w-2xl leading-relaxed">
                        Fifteen of the most common things people want to know before
                        starting a match. If yours isn't here, just reach out.
                    </p>
                </motion.div>
            </section>

            <section className="mx-auto max-w-3xl px-6 lg:px-10 pb-24 lg:pb-32">
                <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                    {faqs.map((item, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            className="border-b border-brand-line"
                            data-testid={`faq-item-${i}`}
                        >
                            <AccordionTrigger className="text-left text-lg lg:text-xl font-medium text-brand-ink py-6 hover:no-underline">
                                <span className="flex items-start gap-6">
                                    <span className="font-serif text-brand-gold text-xl shrink-0 mt-0.5">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{item.q}</span>
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="text-base text-brand-muted leading-relaxed pl-[68px] pb-6">
                                {item.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>
        </div>
    );
}
